import * as React from 'react';
import { toast } from 'react-toastify';
import { ToastComponent, ToastType, ToastOptions } from '@common/components';

import { actions } from 'react-redux-form';
import { Action, Dispatch } from 'redux';

import { realEstateAgentsService } from '@services/RealEstateAgentsService';
import { realEstateService } from '@services/RealEstateService';
import { realEstateOfficesService } from '@services/RealEstateOfficesService';

import { AppState, history } from '..';
import { OptionsViewModel, PageViewModel } from '@models/common.models';
import { RealEstateOfficeViewModel, RealEstateOfficeSearchViewModel } from '@models/RealEstateOffice';
import { AgentSearchViewModel } from '@models/RealEstateAgent';
import { TerritoryViewModel } from '@models/Territory';
import { RealEstateOfficeState } from './realestate-office.reducer';
import FormNames from '@constants/FormNames.enum';
import { RouteDefinitions } from '@common/route-definitions';
import { getRoute } from '../common/route-definitions';

// We can use enums instead of type constants like in the example. This is a newer TS feature
export enum RealEstateOfficeActionType {
  /********** Save **********/
  SaveRealEstateOfficeStart = 'SaveRealEstateOfficeStart',
  SaveRealEstateOfficeSuccess = 'SaveRealEstateOfficeSuccess',
  SaveRealEstateOfficeFailure = 'SaveRealEstateOfficeFailure',

  /********** Get **********/
  GetRealEstateOfficeStart = 'GetRealEstateOfficeStart',
  GetRealEstateOfficeSuccess = 'GetRealEstateOfficeSuccess',
  GetRealEstateOfficeFailure = 'GetRealEstateOfficeFailure',
  GetRealEstateAgentsByOfficeStart = 'GetRealEstateAgentsByOfficeStart',
  GetRealEstateAgentsByOfficeSuccess = 'GetRealEstateAgentsByOfficeSuccess',
  GetRealEstateAgentsByOfficeFailure = 'GetRealEstateAgentsByOfficeFailure',

  /********* Search *******/
  RealEstateOfficeSearchInit = 'RealEstateOfficeSearchInit',
  RealEstateOfficeSearchSuccess = 'RealEstateOfficeSearchSuccess',
  RealEstateOfficeSearchFailure = 'RealEstateOfficeSearchFailure'
}

// This is defining the action definition for certain actions on the page.
// There may be multiple types of these on a given page.
// All properties should be readonly.

interface RealEstateOfficeAction extends Action {
  readonly type: RealEstateOfficeActionType;
}

/********** Save **********/
export interface SaveRealEstateOfficeStartAction extends RealEstateOfficeAction { }
export interface SaveRealEstateOfficeSuccessAction extends RealEstateOfficeAction {
  readonly currentId: number;
}
export interface SaveRealEstateOfficeFailureAction extends RealEstateOfficeAction {
  readonly errorMessage: string;
}

/********** Get **********/
export interface GetRealEstateOfficeStartAction extends RealEstateOfficeAction { }
export interface GetRealEstateOfficeSuccessAction extends RealEstateOfficeAction {
  readonly realEstateOfficeVM: RealEstateOfficeViewModel;
  readonly currentId: number;
}
export interface GetRealEstateOfficeFailureAction extends RealEstateOfficeAction {
  readonly errorMessage: string;
}

export interface GetRealEstateAgentsByOfficeStartAction extends RealEstateOfficeAction { }
export interface GetRealEstateAgentsByOfficeSuccessAction extends RealEstateOfficeAction {
  readonly realEstateAgents: Array<AgentSearchViewModel>;
}
export interface GetRealEstateAgentsByOfficeFailureAction extends RealEstateOfficeAction {
  readonly errorMessage: string;
}

/********** Search **********/
export interface RealEstateOfficeSearchInitAction extends RealEstateOfficeAction {
  readonly query: string | null;
}
export interface RealEstateOfficeSearchSuccessAction extends RealEstateOfficeAction {
  readonly pageNumber: number;
  readonly pageSize: number;
  readonly orderBy: string;
  readonly result: PageViewModel<RealEstateOfficeSearchViewModel>;
}
export interface RealEstateOfficeSearchFailureAction extends RealEstateOfficeAction {
  readonly errorMessage: string;
}

// Actions for each action type declared here.
// They should all return an action object.

/********** Save **********/
export const saveRealEstateOffice = (model: RealEstateOfficeViewModel) => {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(saveRealEstateOfficeStart());
    try {
      if (model.id) {
        await realEstateOfficesService.update(model);
      } else {
        let office = await realEstateOfficesService.create(model);
        model = { ...model, id: office.id };
      }
      dispatch(actions.reset(FormNames.RealEstateOfficeForm));
      dispatch(saveRealEstateOfficeSuccess(model.id));
    } catch (e) {
      dispatch(saveRealEstateOfficeFailure(e.toString()));
    }
  };
};

export function saveRealEstateOfficeStart(): SaveRealEstateOfficeStartAction {
  return {
    type: RealEstateOfficeActionType.SaveRealEstateOfficeStart
  };
}

export function saveRealEstateOfficeSuccess(id: number): SaveRealEstateOfficeSuccessAction {
  toast.success(
    <ToastComponent type={ToastType.Success} message={'Real Estate Office Saved Successfully'} />,
    ToastOptions.Success
  );
  history.push(getRoute(RouteDefinitions.realestateoffice.Details, { id: id }));

  return {
    type: RealEstateOfficeActionType.SaveRealEstateOfficeSuccess,
    currentId: id
  };
}

export function saveRealEstateOfficeFailure(errorMessage: string): SaveRealEstateOfficeFailureAction {
  toast.error(
    <ToastComponent
      type={ToastType.Error}
      message={'Network error, unable to save real estate office. ' + errorMessage}
    />,
    ToastOptions.Error
  );

  return {
    type: RealEstateOfficeActionType.SaveRealEstateOfficeFailure,
    errorMessage: errorMessage
  };
}

/********** Get **********/
export const getRealEstateOffice = (id?: number) => {
  return async (dispatch: Dispatch<AppState>) => {
    if (id) {
      dispatch(getRealEstateOfficeStart());
      try {
        var realEstateOfficeVM = await realEstateOfficesService.get(id);
        dispatch(getRealEstateOfficeSuccess(realEstateOfficeVM));
      } catch (e) {
        dispatch(getRealEstateOfficeFailure(e.toString()));
      }
    }
  };
};
export function getRealEstateOfficeStart(): GetRealEstateOfficeStartAction {
  return {
    type: RealEstateOfficeActionType.GetRealEstateOfficeStart
  };
}
export function getRealEstateOfficeSuccess(
  realEstateOfficeVM: RealEstateOfficeViewModel
): GetRealEstateOfficeSuccessAction {
  return {
    type: RealEstateOfficeActionType.GetRealEstateOfficeSuccess,
    realEstateOfficeVM: realEstateOfficeVM,
    currentId: realEstateOfficeVM.id
  };
}
export function getRealEstateOfficeFailure(errorMessage: string): GetRealEstateOfficeFailureAction {
  toast.error(
    <ToastComponent
      type={ToastType.Error}
      message={'Network error, unable to get real estate office detail. ' + errorMessage}
    />,
    ToastOptions.Error
  );

  return {
    type: RealEstateOfficeActionType.GetRealEstateOfficeFailure,
    errorMessage: errorMessage
  };
}

export function updateModelValue(value: OptionsViewModel) {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(actions.change('realEstateOfficeForm.chwTerritory', value));
  };
}

export function getTerritories(value: string, id: number): Promise<PageViewModel<TerritoryViewModel>> {
  return realEstateService.getTerritories(value, id);
}

export const getRealEstateAgentsByOfficeId = (id: number) => {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(getRealEstateAgentsByOfficeStart());
    try {
      const realEstateAgents: AgentSearchViewModel[] = (await realEstateAgentsService.search(
        undefined,
        [id],
        undefined,
        undefined,
        1,
        100,
        undefined
      )).results;
      dispatch(getRealEstateAgentsByOfficeSuccess(realEstateAgents));
    } catch (e) {
      dispatch(getRealEstateAgentsByOfficeFailure(e.toString()));
    }
  };
};
export function getRealEstateAgentsByOfficeStart(): GetRealEstateAgentsByOfficeStartAction {
  return {
    type: RealEstateOfficeActionType.GetRealEstateAgentsByOfficeStart
  };
}
export function getRealEstateAgentsByOfficeSuccess(
  realEstateAgents: Array<AgentSearchViewModel>
): GetRealEstateAgentsByOfficeSuccessAction {
  return {
    type: RealEstateOfficeActionType.GetRealEstateAgentsByOfficeSuccess,
    realEstateAgents: realEstateAgents
  };
}
export function getRealEstateAgentsByOfficeFailure(errorMessage: string): GetRealEstateAgentsByOfficeFailureAction {
  toast.error(
    <ToastComponent
      type={ToastType.Error}
      message={'Network error, unable to get real estate agents by office. ' + errorMessage}
    />,
    ToastOptions.Error
  );

  return {
    type: RealEstateOfficeActionType.GetRealEstateAgentsByOfficeFailure,
    errorMessage: errorMessage
  };
}

/********** Search **********/
export const realEstateOfficeSearch = (
  query: string | null,
  pageSize: number,
  pageNumber: number,
  orderBy: string,
  orderDirection: string
) => {
  return async (dispatch: Dispatch<RealEstateOfficeState>) => {
    dispatch(realEstateOfficeSearchInit(query));
    try {
      let result: PageViewModel<RealEstateOfficeSearchViewModel> = await realEstateOfficesService.search(
        undefined,
        orderBy,
        orderDirection,
        pageNumber,
        pageSize,
        query ? query : undefined
      );
      dispatch(realEstateOfficeSearchSuccess(pageNumber, pageSize, orderBy, result));
    } catch (e) {
      dispatch(realEstateOfficeSearchFailure(e.toString()));
    }
  };
};
export function realEstateOfficeSearchInit(query: string | null): RealEstateOfficeSearchInitAction {
  return {
    query: query,
    type: RealEstateOfficeActionType.RealEstateOfficeSearchInit
  };
}
export function realEstateOfficeSearchFailure(errorMessage: string): RealEstateOfficeSearchFailureAction {
  toast.error(
    <ToastComponent
      type={ToastType.Error}
      message={'Network error, unable to search real estate offices. ' + errorMessage}
    />,
    ToastOptions.Error
  );

  return {
    errorMessage: errorMessage,
    type: RealEstateOfficeActionType.RealEstateOfficeSearchFailure
  };
}
export function realEstateOfficeSearchSuccess(
  pageNumber: number,
  pageSize: number,
  orderBy: string,
  result: PageViewModel<RealEstateOfficeSearchViewModel>
): RealEstateOfficeSearchSuccessAction {
  return {
    pageNumber: pageNumber,
    pageSize: pageSize,
    orderBy: orderBy,
    result: result,
    type: RealEstateOfficeActionType.RealEstateOfficeSearchSuccess
  };
}



// WEBPACK FOOTER //
// ./src/realestate-office/realestate-office.actions.tsx